import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { ConfirmationModalType } from 'constants/confirmationDialog';

import { openConfirmationDialogAction } from 'store/global/actions';
import {
  deleteServicingEscrowDisbursementAction,
  getServicingSummaryAction,
} from 'store/loanApplications/actions';
import { selectServicingEscrowDisbursements } from 'store/loanApplications/selectors';

import { getEscrowDisbursementsTableConfig } from '../Servicing.config';

import { headerStyles } from './EscrowActivity.styles';
import { EscrowActivityTestIds } from './EscrowActivity.testIds';
import EscrowDisbursementDrawer from './EscrowDisbursementDrawer';

const EscrowActivity = ({ isLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const escrowDisbursements = useSelector(selectServicingEscrowDisbursements);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingDisbursement, setEditingDisbursement] = useState(null);

  const onEdit = useCallback(row => {
    setEditingDisbursement(row);
    setIsDrawerOpen(true);
  }, []);

  const onDelete = useCallback(
    row => {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'Are you sure you want to delete this disbursement?',
          onAcceptButtonName: 'Delete',
          onAccept: () => {
            dispatch(
              deleteServicingEscrowDisbursementAction({
                applicationId,
                disbursementId: row.id,
              }),
            ).then(() => dispatch(getServicingSummaryAction({ applicationId })));
          },
        }),
      );
    },
    [dispatch, applicationId],
  );

  const tableConfig = useMemo(
    () => getEscrowDisbursementsTableConfig({ onEdit, onDelete, isLoading }),
    [onEdit, onDelete, isLoading],
  );

  const onRecordDisbursement = useCallback(() => {
    setEditingDisbursement(null);
    setIsDrawerOpen(true);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingDisbursement(null);
  }, []);

  const onSuccess = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingDisbursement(null);
    dispatch(getServicingSummaryAction({ applicationId }));
  }, [dispatch, applicationId]);

  return (
    <>
      <Box sx={headerStyles}>
        <Button
          color="primary"
          data-testid={EscrowActivityTestIds.RECORD_BUTTON}
          disabled={isLoading}
          variant="contained"
          onClick={onRecordDisbursement}
        >
          Record Disbursement
        </Button>
      </Box>

      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={escrowDisbursements}
        isLoading={isLoading}
        skeletonRows={3}
      />

      <EscrowDisbursementDrawer
        applicationId={applicationId}
        disbursement={editingDisbursement}
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        onSuccess={onSuccess}
      />
    </>
  );
};

EscrowActivity.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(EscrowActivity);
